import { Plus } from 'lucide-react';
import React, { FC } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ImageUpload } from '@/components';
import { useOrganizationState } from '@/state';

import { getBaseOptions, getMediaUploadPath } from '../../config/uploadConfig';
import { countryFlagHelper } from '../../utils';
import { FileCard } from '..';

const Row = styled.div<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  min-height: 56px;
  border-radius: 8px;
  border: 1px solid ${({ $primary, theme }) => {
    if ($primary) return theme.mode === 'dark' ? '#2f4f4f' : '#cfe0e0';
    return theme.mode === 'dark' ? '#374151' : '#e5e7eb';
  }};
  background: ${({ $primary, theme }) => {
    if ($primary) return theme.mode === 'dark' ? 'rgba(26,77,77,0.18)' : '#f3f8f8';
    return theme.mode === 'dark' ? '#1f2937' : '#ffffff';
  }};
`;

const Lang = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 150px;
  flex-shrink: 0;
`;

const FlagCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#f3f4f6')};
  font-size: 15px;
  overflow: hidden;
  flex-shrink: 0;
`;

const LangName = styled.span`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#374151')};
`;

const Action = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
`;

const AddLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 300;
  color: #1a4d4d;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(26, 77, 77, 0.08);
  }
`;

export interface LanguageFileUploadProps {
  language: string;
  value: string;
  onChange: (value: string) => void;
  uploadPath?: string;
  isPrimary?: boolean;
  className?: string;
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
  sk: 'Slovenčina',
  cs: 'Čeština',
  fr: 'Français',
  uk: 'Українська',
  sv: 'Svenska',
  ru: 'Русский',
  it: 'Italiano',
  es: 'Español',
  el: 'Ελληνικά',
  pl: 'Polski',
  nl: 'Nederlands',
};

export const LanguageFileUpload: FC<LanguageFileUploadProps> = ({
  language,
  value,
  onChange,
  isPrimary = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const { organizationId } = useOrganizationState();

  const currentFlag = countryFlagHelper(language);
  const languageName = LANGUAGE_NAMES[language] || language.toUpperCase();
  const hasFile = value && value !== '';

  return (
    <Row className={className} $primary={isPrimary}>
      <Lang>
        <FlagCircle>
          {currentFlag.type === 'iso' ? (
            <ReactCountryFlag svg countryCode={currentFlag.value} />
          ) : (
            <span>{currentFlag.value}</span>
          )}
        </FlagCircle>
        <LangName>{languageName}</LangName>
      </Lang>

      <Action>
        {hasFile ? (
          <FileCard
            fileName={value}
            fileType="PDF"
            onRemove={() => onChange('')}
            onPreview={() => window.open(value, '_blank')}
          />
        ) : (
          <ImageUpload
            options={{
              ...getBaseOptions(false),
              path: getMediaUploadPath(organizationId),
              mimeTypes: ['application/pdf'],
              editor: { images: { crop: false } },
            }}
            onUpload={(fileUrl) => onChange(fileUrl)}
          >
            {({ onClick }) => (
              <AddLink type="button" onClick={onClick}>
                <Plus size={15} />
                {t('upload.uploadFileBtn', 'Dodaj plik')}
              </AddLink>
            )}
          </ImageUpload>
        )}
      </Action>
    </Row>
  );
};
